/**
 * BillingPage Component
 * Main billing page with plan details, usage, and upgrade options
 *
 * Layout:
 * - Page header with help icon
 * - Alert banner (if past-due or trial ending)
 * - Current Plan Card (always visible, top priority)
 * - Usage Details Card (expandable, default collapsed)
 * - Upgrade Options Card (hidden if on highest tier)
 * - Billing History Card (expandable, default collapsed)
 */

import { Layout, Typography, Space, Spin, Alert } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useSubscription } from '@/hooks/useSubscription'
import { useBillingUsage } from '@/hooks/useBillingUsage'
import { CurrentPlanCard } from './CurrentPlanCard'
import { UsageDetailsCard } from './UsageDetailsCard'
import { UpgradeOptionsCard } from './UpgradeOptionsCard'
import { BillingHistoryCard } from './BillingHistoryCard'
import { SubscriptionAlert } from '@/components/billing/SubscriptionAlert'

const { Title, Paragraph } = Typography
const { Content } = Layout

export default function BillingPage() {
  const { data: currentPlan, isLoading: planLoading, error: planError } = useSubscription()
  const { data: usage, isLoading: usageLoading } = useBillingUsage()

  // Show loading spinner while fetching initial data
  if (planLoading || usageLoading) {
    return (
      <Content style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: '16px', color: 'rgba(0, 0, 0, 0.45)' }}>
            Loading your billing information...
          </Paragraph>
        </div>
      </Content>
    )
  }

  // Show error if failed to load
  if (planError || !currentPlan) {
    return (
      <Content style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Alert
          type="error"
          message="Failed to Load Billing Information"
          description="We couldn't retrieve your subscription details. Please try refreshing the page or contact support if the issue persists."
          showIcon
        />
      </Content>
    )
  }

  return (
    <Content style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Page header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>Plan & Billing</Title>
            <Paragraph style={{ margin: '8px 0 0 0', color: 'rgba(0, 0, 0, 0.45)' }}>
              Manage your subscription, usage, and billing
            </Paragraph>
          </div>
          <a
            href="https://help.rocketpricing.com/billing"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Billing help documentation"
          >
            <QuestionCircleOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
          </a>
        </div>

        {/* Alert banner (past-due, trial ending, etc.) */}
        <SubscriptionAlert currentPlan={currentPlan} usage={usage} />

        {/* Current Plan Card (always first, most important) */}
        <CurrentPlanCard currentPlan={currentPlan} usage={usage} />

        {/* Usage Details Card (expandable, default collapsed) */}
        <UsageDetailsCard usage={usage} />

        {/* Upgrade Options Card (hidden if on business tier) */}
        {currentPlan.tier !== 'business' && (
          <UpgradeOptionsCard currentTier={currentPlan.tier} />
        )}

        {/* Billing History Card (expandable, default collapsed) */}
        <BillingHistoryCard />
      </Space>
    </Content>
  )
}
